import * as React from 'react';
import { observer, inject } from 'mobx-react';
import * as dateFns from 'date-fns';
import * as dedent from 'dedent';
import * as Ajv from 'ajv';

import { ViewableEvent } from '../../types';
import { saveFile, uploadFile, Ctrl } from '../../util/ui';
import { styled } from '../../styles';

import { AccountStore } from '../../model/account/account-store';
import { EventsStore } from '../../model/events/events-store';
import { UiStore } from '../../model/ui/ui-store';
import { generateHar } from '../../model/http/har';
import { logError } from '../../errors';
import { formatAjvError } from '../../util/json-schema';
import { DesktopApi } from '../../services/desktop-api';

import { IconButton } from '../common/icon-button';

const UTF8Decoder = new TextDecoder('utf8', { fatal: true });

export const ClearAllButton = observer((props: {
    className?: string,
    disabled: boolean,
    onClear: () => void
}) => <IconButton
    icon={['far', 'trash-alt']}
    title={`Clear all (${Ctrl}+Shift+Delete)`}
    disabled={props.disabled}
    onClick={props.onClear}
/>);

export const ExportAsHarButton = inject('accountStore')(observer((props: {
    className?: string,
    accountStore?: AccountStore,
    events: ReadonlyArray<ViewableEvent>
}) => {
    const isPaidUser = props.accountStore!.user.isPaidUser();

    return <IconButton
        icon={['fas', 'save']}
        title={
            isPaidUser
                ? 'Export these exchanges as a HAR file'
                : (
                    'With Pro: Export requests & responses as a HAR file, ' +
                    'to save for later or share with others'
                )
        }
        disabled={!isPaidUser || props.events.length === 0}
        onClick={async () => {
            const harContent = JSON.stringify(
                await generateHar(props.events)
            );
            const filename = `HTTPToolkit_${
                dateFns.format(Date.now(), 'YYYY-MM-DD_HH-mm')
            }.har`;

            saveFile(filename, 'application/har+json;charset=utf-8', harContent);
        }}
    />
}));

// Sanitise a string for use as part of a filesystem filename. Anything outside the
// shell's allowlist ([A-Za-z0-9._-]) becomes an underscore, and we cap the length
// so a very long URL host doesn't blow past OS filename limits.
const filenameSafe = (s: string, max = 40) =>
    s.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, max) || 'unknown';

// Save each currently-filtered HTTP request as its own JSON file inside a folder
// the user picks. Only visible when running inside the desktop shell (needs the
// new selectDirectory / writeJsonFile IPCs).
export const SaveRequestsToFolderButton = observer((props: {
    className?: string,
    events: ReadonlyArray<ViewableEvent>
}) => {
    const canSave = !!DesktopApi.selectDirectory && !!DesktopApi.writeJsonFile;
    const httpExchanges = props.events.filter(e => e.isHttp());

    return <IconButton
        icon={['fas', 'download']}
        title={
            canSave
                ? 'Save each filtered request as a JSON file in a folder'
                : 'Save requests to folder (only available in the desktop app)'
        }
        disabled={!canSave || httpExchanges.length === 0}
        onClick={async () => {
            // Capture as local consts so TS narrowing survives awaits below.
            const { selectDirectory, writeJsonFile } = DesktopApi;
            if (!selectDirectory || !writeJsonFile) return;

            const folder = await selectDirectory();
            if (!folder) return; // user cancelled

            let saved = 0;
            const errors: string[] = [];

            for (const exchange of httpExchanges) {
                const request = exchange.request;
                try {
                    // Wait for the body to decode (no-op if already decoded).
                    // Same accessor path the HAR export uses.
                    await request.body.waitForDecoding().catch(() => {});

                    let requestBody: string | null = null;
                    if (request.body.isDecoded() && request.body.decodedData) {
                        const buf = request.body.decodedData;
                        try {
                            requestBody = UTF8Decoder.decode(buf);
                        } catch {
                            // Not UTF-8 (e.g. gzip'd or binary) — fall back to base64
                            requestBody = buf.toString('base64');
                        }
                    }

                    const timestamp = new Date().toISOString();
                    const filenameTs = timestamp.replace(/[:.]/g, '-');
                    const hostSlug = filenameSafe(request.parsedUrl.host || 'unknown');
                    const methodSlug = filenameSafe(request.method || 'REQ', 10);
                    const idSlug = filenameSafe(exchange.id.slice(0, 8));
                    const filename = `${filenameTs}_${methodSlug}_${hostSlug}_${idSlug}.json`;

                    const payload = JSON.stringify({
                        timestamp,
                        url: request.parsedUrl.toString(),
                        requestHeaders: request.headers,
                        requestBody
                    }, null, 2);

                    await writeJsonFile(folder, filename, payload);
                    saved++;
                } catch (e: any) {
                    errors.push(`${request.method} ${request.parsedUrl.toString()}: ${e?.message || e}`);
                    logError(e);
                }
            }

            if (errors.length === 0) {
                alert(`Saved ${saved} request${saved === 1 ? '' : 's'} to:\n${folder}`);
            } else {
                alert(
                    `Saved ${saved} of ${httpExchanges.length} requests to:\n${folder}\n\n` +
                    `${errors.length} failed:\n${errors.slice(0, 5).join('\n')}` +
                    (errors.length > 5 ? `\n…and ${errors.length - 5} more` : '')
                );
            }
        }}
    />;
});

// Styled variant of IconButton that shows a distinctive active colour when auto-save
// is running, so the user can see at a glance that requests are being written to disk.
const AutoSaveIconButton = styled(IconButton)<{ active?: boolean }>`
    ${p => p.active && `
        color: ${p.theme.popColor};
        &:not([disabled]):hover, &:not([disabled]):focus {
            color: ${p.theme.popColor};
        }
    `}
`;

// Continuous rule: while enabled, every newly captured HTTP exchange that matches
// the current filter box (uiStore.activeFilterSet) is auto-saved to the chosen folder.
// See installAutoSave() in model/ui/auto-save-manager.ts for the observer that does
// the actual writing — this button just toggles the enabled flag.
export const AutoSaveToggleButton = inject('uiStore')(observer((props: {
    className?: string,
    uiStore?: UiStore
}) => {
    const uiStore = props.uiStore!;
    const canSave = !!DesktopApi.selectDirectory && !!DesktopApi.writeJsonFile;
    const isActive = uiStore.autoSaveEnabled;

    const title = !canSave
        ? 'Auto-save requests to folder (only available in the desktop app)'
        : isActive
            ? `Auto-saving matching requests to:\n${uiStore.autoSaveFolder}\n\nClick to stop.`
            : 'Auto-save each matching request to a folder as it is captured…';

    return <AutoSaveIconButton
        icon={['fas', 'sync-alt']}
        title={title}
        active={isActive}
        disabled={!canSave}
        onClick={async () => {
            if (uiStore.autoSaveEnabled) {
                uiStore.disableAutoSave();
                return;
            }

            if (!DesktopApi.selectDirectory) return;
            const folder = await DesktopApi.selectDirectory();
            if (!folder) return; // user cancelled

            uiStore.enableAutoSave(folder);
        }}
    />;
}));

export const ImportHarButton = inject('eventsStore', 'accountStore')(
    observer((props: {
        accountStore?: AccountStore,
        eventsStore?: EventsStore
    }) => {
        const isPaidUser = props.accountStore!.user.isPaidUser();

        return <IconButton
            icon={['fas', 'folder-open']}
            title={
                isPaidUser
                    ? 'Import exchanges from a HAR file'
                    : (
                        'With Pro: Import requests & responses from HAR files, ' +
                        'to examine past recordings or data from other tools'
                    )
            }
            disabled={!isPaidUser}
            onClick={async () => {
                const uploadedFile = await uploadFile('text', ['.har', 'application/har', 'application/har+json']);
                if (uploadedFile) {
                    let data: {};
                    try {
                        data = JSON.parse(uploadedFile);
                        await props.eventsStore!.loadFromHar(data);
                    } catch (error: any) {
                        logError(error);

                        if (error.name === 'HARError' && error.errors) {
                            alert(dedent`
                                HAR file is not valid.

                                ${
                                    error.errors
                                    .map((e: Ajv.ErrorObject) => formatAjvError(data, e))
                                    .join('\n')
                                }
                            `);
                        } else {
                            alert(dedent`
                                Could not parse HAR file.

                                ${error.message || error}
                            `);
                        }
                    }
                }
            }}
        />
    })
);

export const PlayPauseButton = inject('eventsStore')(
    observer((props: {
        eventsStore?: EventsStore
    }) => {
        const { isPaused, togglePause } = props.eventsStore!;

        return <IconButton
            icon={['fas', isPaused ? 'play' : 'pause']}
            title={`${isPaused ? 'Resume' : 'Pause'} collecting intercepted exchanges`}
            onClick={togglePause}
        />
    })
);

export const ScrollToEndButton = (props: { onScrollToEnd: () => void }) =>
    <IconButton
        icon={['fas', 'level-down-alt']}
        title="Scroll to the bottom of the list"
        onClick={props.onScrollToEnd}
    />